import * as _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import LeaseGenerationTractDetail from 'src/models/leasing/LeaseGenerationTractDetail';
import { Runsheet } from 'src/models/Runsheets';
import User from 'src/models/User';
import useFetch from 'use-http';
import RunsheetDetails from './RunsheetDetails';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import CurativeModule from './TractComponents/CurativeModule';
import IndexReferenceType from 'src/models/IndexReferenceType';
import CurativeRequirement from 'src/models/CurativeRequirement';
import Landman from 'src/models/Landman';
import RunsheetTractListSlim from './TractComponents/RunsheetTractListSlim';
import CollapsibleSection from 'src/components/common/CollapsibleSection';

interface IRunsheetDetailsDialogStateProps {
    runsheet: Runsheet;
    seededTractSearchTerm?: string;
    selectedTract?: number;
    users: User[];
    projectTracts: LeaseGenerationTractDetail[];
    name: string;
    title: string;
    projectNumber: number;
    clientNumber: number;
    curativeStatuses: Array<IndexReferenceType>;
    projectStaff: Array<Landman>;
    loadingTracts: boolean;
    finalizeData: (data: Runsheet) => Runsheet;
    finalizeCurativeRequirements: (data: CurativeRequirement[]) => CurativeRequirement[];
    incrementProjectTracts?: () => void;
    setProjectTractsSearchTerm?: (searchTerm: string) => void;
    navigateToTractDetail?: (tractId: number) => void;
    onCloseDialog?: () => void;
}

const RunsheetDetailsDialog = (props: IRunsheetDetailsDialogStateProps) => {
    const { register, errors, handleSubmit, reset, control, getValues, formState } = useForm();
    const { response, post, put, del } = useFetch();
    const [selectedTracts, setSelectedTracts] = useState<number[]>([]);
    const [tractList, setTractList] = useState<Array<LeaseGenerationTractDetail>>([]);
    const [sortedTractList, setSortedTractList] = useState<Array<LeaseGenerationTractDetail>>([]);
    const [curativeRequirements, setCurativeRequirements] = useState<CurativeRequirement[]>([]);
    const [curativeDeleteIds, setCurativeDeleteIds] = useState<number[]>([]);
    const [pageUpdated, setPageUpdated] = useState<boolean>(false);
    const [updateText, setUpdateText] = useState<string>('');

    useEffect(() => {
        return () => reset();
    }, []);

    useEffect(() => {
        if (!!props?.seededTractSearchTerm && props?.seededTractSearchTerm?.length > 0) {
            props.setProjectTractsSearchTerm(props.seededTractSearchTerm);
        }
    }, [props.seededTractSearchTerm]);

    useEffect(() => {
        if (!!props.selectedTract) {
            onUpdateSelectedTractIds(props.selectedTract);
        }
    }, [props.selectedTract]);

    useEffect(() => {
        reset(props.runsheet);
        setCurativeRequirements(props.runsheet?.curativeRequirements);
        setSelectedTracts(props.runsheet?.attachedTractIds ? props.runsheet.attachedTractIds : []);
    }, [props.runsheet]);

    useEffect(() => {
        if (!!props?.projectTracts && props?.projectTracts?.length > 0) {
            setTractList(props.projectTracts);
        }
    }, [props.projectTracts]);

    useEffect(() => {
        if (!!tractList && tractList.length > 0) {
            sortProjectTracts();
        }
    }, [tractList]);

    useEffect(() => {
        if (!!selectedTracts && selectedTracts.length > 0) {
            sortProjectTracts();
        }
    }, [selectedTracts]);

    useEffect(() => {
        if (pageUpdated) {
            setTimeout(() => {
                setPageUpdated(false);
                setUpdateText('');
            }, 2000);
        }
    }, [pageUpdated]);

    const createRunsheet = async (newRunsheet: Runsheet) => {
        let newId = await post(`/v1.0/clients/${props.clientNumber}/projects/${props.projectNumber}/runsheet`, newRunsheet);

        if (response.ok) {
            updateAssociatedTracts(selectedTracts, newId);
        } else {
            console.log('Unable to Create Runsheet', response);
        }
    };

    const updateRunsheet = async (newRunsheet: Runsheet) => {
        await put(`/v1.0/clients/${props.clientNumber}/projects/${props.projectNumber}/runsheet`, newRunsheet);

        if (response.ok) {
            updateAssociatedTracts(selectedTracts, newRunsheet.id);
        } else {
            console.log('Unable to Edit Runsheet', response);
        }
    };

    const addUpdateCurativeRequirements = async (curatives: CurativeRequirement[]) => {
        await post(`/v1.0/clients/${props.clientNumber}/projects/${props.projectNumber}/runsheet/curative-requirements`, curatives);

        if (response.ok) {
            setUpdateText('Curative Requirements updated successfully!');
            setPageUpdated(true);
        } else {
            console.log('Unable to update Curative Requirements.', response);
        }
    };

    const deleteCurativeRequirement = async (curativeId: number) => {
        await del(`/v1.0/clients/${props.clientNumber}/projects/${props.projectNumber}/runsheet/curative-requirements/${curativeId}`);

        if (!(response.ok)) {
            console.log('Unable to update Curative Requirements.', response);
        }
    };

    const onSubmit = (data: Runsheet, event: React.FormEvent<HTMLFormElement>) => {
        event.stopPropagation();

        data = props.finalizeData(data);

        if (data.id === 0) {
            createRunsheet(data);
        } else {
            updateRunsheet(data);
        }
    };

    const sortProjectTracts = () => {
        let newTractList = [...tractList];
        let selectedTractList: LeaseGenerationTractDetail[] = [];
        selectedTracts.map((st: number) => {
            let selectedTract = tractList.find((td) => st === td?.id);
            if (!!selectedTract) {
                selectedTractList.push(selectedTract);
            }
        });

        for (let index = 0; index < selectedTractList.length; index++) {
            newTractList.splice(newTractList.indexOf(selectedTractList[index]), 1);
            newTractList.unshift(selectedTractList[index]);
        }

        setSortedTractList(newTractList);
    };

    const updateAssociatedTracts = async (tractsList: Array<number>, runsheetId: number) => {
        await put(`/v1.0/clients/${props.clientNumber}/projects/${props.projectNumber}/runsheet/${runsheetId}/tracts`, tractsList);

        if (response.ok) {
            props.onCloseDialog();
        } else {
            console.log('Unable to attach tracts', response);
        }
    };

    const showSelectedGlobalIds = () => {
        if (selectedTracts?.length > 0) {
            return selectedTracts.map((selectedTract: number, index) => {
                const tempTract = props.projectTracts?.find((x) => x.id === selectedTract);
                if (tempTract) {
                    const tempGlobalId = `${tempTract.sectionNumber}.${tempTract.tractNumber}`;
                    return (
                        <>
                            <span>{tempGlobalId}</span>
                            {index < selectedTracts.length - 1 ? <span>, </span> : null}
                        </>
                    );
                } else {
                    return <span />;
                }
            });
        } else {
            return <span />;
        }
    };

    // TODO: Consider abstracting this. It looks like it works more efficiently than CardContainer's logic.
    const getScrollStatus = (divElement: HTMLDivElement) => {
        if (props.loadingTracts !== true && divElement) {
            const scrollTop = divElement.scrollTop % 1 > .9 ? divElement.scrollTop : Math.round(divElement.scrollTop);
            const scrollHeight = divElement.scrollHeight;
            const clientHeight = divElement.clientHeight;

            // Check if reached the end of the list
            if (scrollTop + clientHeight >= scrollHeight - 5/*This is because I've seen it be just +1 difference*/) {
                props.incrementProjectTracts();
            }
        }
    };

    const onUpdateSelectedTractIds = (tractId: number) => {
        let newSelectedTractIds = [];
        if (selectedTracts.indexOf(tractId) > -1) {
            newSelectedTractIds = selectedTracts.filter((x) => x !== tractId);
        } else {
            newSelectedTractIds = [...selectedTracts, tractId];
        }
        setSelectedTracts(newSelectedTractIds);
    };
    
    const projectTractSearch = _.debounce((searchContent: string) => {
        props.setProjectTractsSearchTerm(searchContent);
    }, 800);

    const navigateToTractDetail = (tractId: number) => {
        props.navigateToTractDetail(tractId);
    };

    const addCurativeRequirement = () => {
        let newForm = getValues() as Runsheet;
        if (newForm.curativeRequirements?.length > 0) {
            newForm.curativeRequirements.push(new CurativeRequirement());
        } else {
            newForm.curativeRequirements = [new CurativeRequirement()];
        }
        setCurativeRequirements(newForm.curativeRequirements);
    };

    const removeCurativeRequirement = (index: number) => {
        let newForm = getValues() as Runsheet;
        if (newForm.curativeRequirements[index]?.id > 0) {
            let deleteIds = [...curativeDeleteIds];
            deleteIds.push(parseInt(newForm.curativeRequirements[index].id + '', 10));
            setCurativeDeleteIds(deleteIds);
        }
        newForm.curativeRequirements.splice(index, 1);
        setCurativeRequirements(newForm.curativeRequirements);
        reset(newForm);
    };

    const updateCurativeRequirements = () => {
        let newForm = getValues() as Runsheet;
        if (newForm.curativeRequirements?.length > 0) {
            let formatted = props.finalizeCurativeRequirements(newForm.curativeRequirements);
            addUpdateCurativeRequirements(formatted);
        }
        if (curativeDeleteIds.length > 0) {
            for (let deleteId of curativeDeleteIds) {
                deleteCurativeRequirement(deleteId);
            }
        }
    };

    const cancelFormChanges = () => {
        props.onCloseDialog();
    };

    return (
        <>
            <Dialog title={props.title} dialogName={props.name} onCloseDialog={props.onCloseDialog}>
                <div style={{ maxHeight: '90vh', overflow: 'auto' }}>
                    {(pageUpdated || formState.isDirty) && (
                        <div
                            style={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 10,
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {pageUpdated && (
                                <div
                                    style={{
                                        backgroundColor: 'rgba(0,150,0,.50)',
                                        color: 'white',
                                        padding: '1rem',
                                        textAlign: 'center',
                                    }}
                                >
                                    {updateText}
                                </div>
                            )}

                            {formState.isDirty && (
                                <div
                                    style={{
                                        backgroundColor: 'rgba(255,0,0,.50)',
                                        color: 'white',
                                        padding: '1rem',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        position: 'relative',
                                    }}
                                >
                                    <div>Unsaved Changes - Please Save Before Leaving Form</div>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            right: '.5rem',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            display: 'flex',
                                        }}
                                    >
                                        <Button 
                                            text="Save" 
                                            type="submit" 
                                            size="small" 
                                            intent="primary" 
                                            disabled={formState.isSubmitting} 
                                            onClick={handleSubmit(onSubmit)}
                                        />
                                        <div style={{ margin: '0 1rem', borderRight: '1px solid white', width: '1px' }} />
                                        <Button
                                            text="Cancel Changes"
                                            size="small"
                                            disabled={!formState.isDirty || formState.isSubmitting}
                                            onClick={cancelFormChanges}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <form onSubmit={handleSubmit(onSubmit)} style={{ minWidth: '80vw' }}>
                        <CollapsibleSection title={props.title + ' Details'} startCollapsed={false}>
                            <div style={{ display: 'flex', width: '80vw', height: '70vh' }}>
                                <div style={{ width: '60%', borderRight: '1px solid grey', margin: '.5rem', paddingRight: '1rem' }}>
                                    {props.runsheet && (
                                        <>
                                            <div style={{ overflow: 'auto', maxHeight: '100%' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <div>Note</div>
                                                    <div>
                                                        <span style={{ marginRight: '1rem' }}>Attached Tracts:</span>
                                                        {showSelectedGlobalIds()}
                                                    </div>
                                                </div>
                                                <RunsheetDetails register={register} errors={errors} control={control} projectStaff={props.projectStaff} />
                                                <br />
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div style={{ width: '40%', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ fontSize: '20px', fontWeight: 'bold' }}>Tract List</div>
                                    <div style={{ display: 'flex' }}>
                                        <label style={{ paddingRight: '8px', whiteSpace: 'nowrap' }}>Search Project Tracts:</label>
                                        <RHFInputBox
                                            style={{ height: '20px' }}
                                            onChange={(event) => projectTractSearch(event.currentTarget.value)}
                                            name="runsheetsearch"
                                            register={register}
                                        />
                                    </div>
                                    <div
                                        onScroll={(event) => getScrollStatus(event.currentTarget)}
                                        style={{ margin: '.5rem', width: '100%', overflowX: 'hidden', overflowY: 'auto', maxHeight: '90vh' }}
                                    >
                                        <div style={{ overscrollBehavior: 'contain', paddingTop: '.5rem' }}>
                                            <RunsheetTractListSlim
                                                tracts={sortedTractList}
                                                updateSelectedTractIds={onUpdateSelectedTractIds}
                                                selectedTractIds={selectedTracts}
                                                navigateToTractDetail={(id) => navigateToTractDetail(id)}
                                                loadingTracts={props.loadingTracts}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', position: 'sticky', paddingTop: '5px' }}>
                                <Button text="Save" type="submit" intent="primary" />
                            </div>
                            <div
                                className={`dlc-section-bar`}
                                style={{
                                    position: 'relative',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            />
                            <br />
                        </CollapsibleSection>
                        <br />
                        <CollapsibleSection title="Curative Module" startCollapsed={true}>
                            <CurativeModule
                                curativeRequirements={curativeRequirements}
                                runsheetId={props.runsheet?.id}
                                curativeStatuses={props.curativeStatuses}
                                projectStaff={props.projectStaff}
                                addRow={addCurativeRequirement}
                                deleteRow={removeCurativeRequirement}
                                updateCurativeRequirements={updateCurativeRequirements}
                                register={register}
                                errors={errors}
                            />
                            <div
                                className={`dlc-section-bar`}
                                style={{
                                    position: 'relative',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            />
                        </CollapsibleSection>
                    </form>
                </div >
            </Dialog >
        </>
    );
};

export default RunsheetDetailsDialog;


// WEBPACK FOOTER //
// ./src/components/project/sections/RunsheetDetailsDialog.tsx